// Quote PDF action cluster — generates a PDF via shared/quotePdf and offers
// preview / download / Outlook draft. Self-contained: takes the already-loaded
// quote+customer+parts as props and emits an optional onPreview(blob) callback
// so the caller wires its own PdfViewerModal. Integration into
// QuoteDetailPage's action row happens in a follow-up pass.
//
// Email-draft note (M2 status): src/shared/graph.ts → saveToOutlookDrafts()
// does NOT yet support file attachments. Until the Graph helper grows an
// `attachments` arg (planned for M3 — the OneDrive + Outlook attachment
// pipeline), the email button still GENERATES the PDF so Justin can grab the
// download and hand-attach in Outlook, but it just alerts that the auto-attach
// is on the roadmap. No outbound mail is sent — per the
// [[feedback-drafts-never-autosend]] hard rule, this would only ever draft.
//
// Style: mirrors NewQuoteModal / NewPartModal button conventions — orange
// primary, line-dark secondary, px-3 py-1.5 rounded, semibold text-sm.

import { useState } from 'react';
import { generateQuotePdf } from '@/shared/quotePdf';
import type { Quote, Customer, Part } from '@/shared/crmTypes';

export interface QuotePdfActionsProps {
  quote: Quote;
  /** Pass `null` if the customer record isn't loaded — the PDF will render a
   *  "Customer not loaded" placeholder banner instead of bailing out. */
  customer: Customer | null;
  /** Parts already resolved by the caller (the detail page typically
   *  pre-loads these so the table renders without a second fetch). */
  parts: Part[];
  /** When set, the "Generate PDF" button hands the blob to the caller instead
   *  of triggering a download. Lets the caller open its own modal viewer
   *  (PdfViewerModal). When omitted, "Generate PDF" falls back to a download. */
  onPreview?: (blob: Blob) => void;
}

/**
 * Synthesize a minimal Customer when the caller passed `null` so the PDF
 * generator (which requires a non-null Customer) still renders a usable
 * letterhead with a clear "not loaded" marker. Matches the customer-name
 * snapshot the quote already carries so the rep still sees who the quote
 * was for.
 */
function fallbackCustomer(quote: Quote): Customer {
  return {
    id: quote.customerId || 'unknown',
    code: '',
    companyName: quote.customerNameSnapshot || 'Customer not loaded',
    contacts: [],
    billingAddress: {
      line1: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
    },
    terms: 'Net 30',
    taxExempt: false,
    tier: 'new',
    tierManualOverride: false,
    createdAt: quote.createdAt,
    updatedAt: quote.updatedAt,
    _v: 1,
    _audit: [],
  };
}

/** Sanitize the company name for use as a filename segment. */
function fileSafe(s: string): string {
  return s.replace(/\W+/g, '-').replace(/^-+|-+$/g, '') || 'no-customer';
}

function buildFilename(quote: Quote, customer: Customer | null): string {
  const id = quote.number || quote.id;
  const co = fileSafe(customer?.companyName ?? '');
  return `Quote-${id}-${co}.pdf`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari/Firefox have time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type Mode = 'preview' | 'download' | 'email';

export function QuotePdfActions({
  quote,
  customer,
  parts,
  onPreview,
}: QuotePdfActionsProps) {
  // Single busy flag covers all three buttons — generation is the bottleneck
  // and we don't want two parallel jsPDF loads racing against each other.
  const [busy, setBusy] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(mode: Mode): Promise<void> {
    setBusy(mode);
    setError(null);
    try {
      const cust = customer ?? fallbackCustomer(quote);
      const blob = await generateQuotePdf(quote, cust, parts);
      const filename = buildFilename(quote, customer);

      if (mode === 'preview') {
        if (onPreview) {
          onPreview(blob);
        } else {
          // No preview wired — fall back to a download so the click still
          // produces something useful instead of disappearing.
          triggerDownload(blob, filename);
        }
      } else if (mode === 'download') {
        triggerDownload(blob, filename);
      } else {
        // mode === 'email'
        // MS Graph attachments not wired yet (see file-header note). Trigger
        // a download too so the rep can manually drag-attach in Outlook.
        triggerDownload(blob, filename);
        alert('TODO: PDF attachment via MS Graph — coming in M3');
      }
    } catch (e) {
      setError(`PDF action failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  const disabled = busy !== null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => void run('preview')}
          disabled={disabled}
          className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {busy === 'preview' ? 'Generating…' : '📄 Generate PDF'}
        </button>
        <button
          type="button"
          onClick={() => void run('download')}
          disabled={disabled}
          className="px-3 py-1.5 bg-paper border border-line-dark text-text text-sm rounded font-semibold hover:bg-bg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy === 'download' ? 'Generating…' : '⬇ Download'}
        </button>
        <button
          type="button"
          onClick={() => void run('email')}
          disabled={disabled}
          className="px-3 py-1.5 bg-paper border border-line-dark text-text text-sm rounded font-semibold hover:bg-bg disabled:opacity-50 disabled:cursor-not-allowed"
          title="Generates the PDF and (eventually) drafts an Outlook email with it attached. For now, attach the downloaded file manually."
        >
          {busy === 'email' ? 'Generating…' : '📧 Email draft'}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm"
        >
          ⚠ {error}
        </div>
      )}
    </div>
  );
}
